import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { type ChannelId } from '@onr/schema'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser'
import { type MessagingRailProps } from '@/components/organisms/MessagingRail'
import { type ChannelViewProps } from '@/components/templates/ChannelView'
import { channelDeletionsQuery, channelMessagesQuery } from '@/state/channels/channel'
import { eveningTeamsQuery } from '@/state/regions/regions'

import { channelMuteLabel, channelName } from './channelLabels'
import { toChannelMessageItems } from './channelMessageItems'

export interface UseEveningChannelPageOptions {
  eveningId: number
  channelId: ChannelId
  regionId?: string
  regionName: string
  piloteRegionId: number
  rail: MessagingRailProps
}

export const useEveningChannelPage = ({
  eveningId,
  channelId,
  regionId,
  regionName,
  piloteRegionId,
  rail,
}: UseEveningChannelPageOptions): ChannelViewProps => {
  const intl = useIntl()
  const { sys_admin: isSysAdmin } = useAuthenticatedFirebaseUser()
  const { data: teamsSnapshot } = useSuspenseQuery(eveningTeamsQuery(eveningId))
  // The historical link does not always carry the region, so the evening's own teams supply it.
  const resolvedRegionId = regionId ?? teamsSnapshot.data[0]?.regionId
  const { data: messages } = useQuery(
    channelMessagesQuery(resolvedRegionId ?? '', channelId, resolvedRegionId ? eveningId : undefined)
  )
  const { data: deletions } = useQuery(
    channelDeletionsQuery(resolvedRegionId ?? '', channelId, resolvedRegionId ? eveningId : undefined, isSysAdmin)
  )

  const messageItems = useMemo(
    () =>
      toChannelMessageItems({
        messages: messages?.data ?? [],
        teams: teamsSnapshot.data,
        intl,
        regionName,
        deletions: deletions?.data,
      }),
    [messages?.data, teamsSnapshot.data, intl, regionName, deletions?.data]
  )

  return {
    rail,
    channelName: channelName(intl, channelId),
    muteLabel: channelMuteLabel(intl, channelId, false),
    regionName,
    piloteRegionId,
    readOnly: true,
    eveningOpen: false,
    hasUnread: false,
    messages: messageItems,
    canDelete: false,
  }
}
